import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { ADD_HIKE, DELETE_HIKE, ENRICH_TRAIL, GET_HIKE, GET_HIKES, SEARCH_TRAILS, UPDATE_HIKE } from './operations';
import HikingForm from './HikingForm';
import TrailAddSearch from './TrailAddSearch';
import { hikingStrings, moduleCommonStrings } from '../../content/hobbyModules';
import { GET_TRIPS } from '../traveling/operations';
import PremiumUpsellModal from '../../components/PremiumUpsellModal';
import ModuleListSkeleton from '../../components/common/ModuleListSkeleton';
import DetailActionMenu from '../../components/common/DetailActionMenu';
import EntryImage from '../../components/common/EntryImage';
import HobbyEmptyState from '../../components/common/HobbyEmptyState';
import RatingReadout from '../../components/common/RatingReadout';
import { GET_MY_SUBSCRIPTION_STATUS } from '../../views/account/operations';
import { getPublicErrorMessage, isLimitReachedError } from '../../utils/errors';
import { canCreateEntries } from '../../utils/subscriptionLimits';
import { fileToBase64 } from '../../components/EntryPhotoPicker';
import { ADD_ENTRY_HIGHLIGHT_PHOTO, REMOVE_ENTRY_COVER_PHOTO, SAVE_ENTRY_PHOTO_ALBUM_LINK, UPLOAD_ENTRY_COVER_PHOTO } from '../entryPhotos/operations';
import { saveCreatedAlbumLink } from '../entryPhotos/albumDraft';
import { entryPhotoStrings } from '../../content/entryPhotos';
import PhotoAlbumsSection from '../entryPhotos/PhotoAlbumsSection';
import EntryChecklistProgress from '../checklists/EntryChecklistProgress';
import { shareHighlightStrings } from '../../content/shareHighlights';
import { buildShareHighlight, openShareHighlight } from '../shareHighlights/shareHighlight';

const HIKING_PAGE_SIZE = 20;

const defaultHike = {
  title: '',
  location: '',
  hikeDate: '',
  status: 'Planned',
  distance: '',
  distanceUnit: 'mi',
  elevationGain: 0,
  elevationUnit: 'ft',
  durationMinutes: 0,
  difficulty: 'Moderate',
  trailType: 'Loop',
  terrain: '',
  latitude: null,
  longitude: null,
  hasRouteGeometry: false,
  geometrySummary: '',
  routeUrl: '',
  mapUrl: '',
  permitRequired: false,
  dogFriendly: false,
  kidFriendly: false,
  rating: null,
  favorite: false,
  startTime: '',
  parkingNotes: '',
  companions: '',
  weather: '',
  trailCondition: '',
  crowdLevel: '',
  actualDistance: '',
  actualDurationMinutes: 0,
  notes: '',
  checklist: [],
  tags: [],
  tripId: '',
  tripTitle: '',
  tripDayId: '',
  tripDayTitle: '',
  trailCatalogId: '',
  trailSource: '',
  trailSourceId: '',
  coverPhotoUrl: '',
  coverPhotoUpdatedAt: '',
};

/**
 * Performs the split csv operation.
 *
 * @param {*} value - Value to inspect or transform.
 * @returns {*} The derived operation result.
 */
function splitCsv(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  return String(value || '').split(',').map((item) => item.trim()).filter(Boolean);
}

/**
 * Normalizes hike.
 *
 * @param {*} hike - hike value.
 * @returns {*} The derived operation result.
 */
function parseHike(hike) {
  const safeHike = hike || {};
  return {
    ...defaultHike,
    ...safeHike,
    title: safeHike.title || hikingStrings.module.untitled,
    elevationGain: Number(safeHike.elevationGain || 0),
    durationMinutes: Number(safeHike.durationMinutes || 0),
    actualDurationMinutes: Number(safeHike.actualDurationMinutes || 0),
    checklist: Array.isArray(safeHike.checklist) ? safeHike.checklist.filter((item) => item?.title) : [],
    tags: Array.isArray(safeHike.tags) ? safeHike.tags.filter(Boolean) : splitCsv(safeHike.tags),
    favorite: Boolean(safeHike.favorite),
    permitRequired: Boolean(safeHike.permitRequired),
    dogFriendly: Boolean(safeHike.dogFriendly),
    kidFriendly: Boolean(safeHike.kidFriendly),
  };
}

/**
 * Performs the unique values operation.
 *
 * @param {Array<*>} items - items collection.
 * @param {*} getter - getter value.
 * @returns {*} The derived operation result.
 */
function uniqueValues(items, getter) {
  return Array.from(new Set(items.flatMap(getter).filter(Boolean))).sort();
}

/**
 * Normalizes date.
 *
 * @param {*} value - Value to inspect or transform.
 * @returns {*} The derived operation result.
 */
function formatDate(value) {
  if (!value) return hikingStrings.module.noDate;
  return new Date(`${value}T00:00:00`).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Performs the duration label operation.
 *
 * @param {*} value - Value to inspect or transform.
 * @returns {*} The derived operation result.
 */
function durationLabel(value) {
  const minutes = Number(value || 0);
  return minutes ? `${minutes} min` : hikingStrings.module.noDuration;
}

/**
 * Performs the distance label operation.
 *
 * @param {*} hike - hike value.
 * @returns {*} The derived operation result.
 */
function distanceLabel(hike) {
  return hike.distance ? `${hike.distance} ${hike.distanceUnit || ''}`.trim() : hikingStrings.module.noDistance;
}

/**
 * Determines whether coordinates.
 *
 * @param {*} hike - hike value.
 * @returns {boolean} Whether the requested condition is satisfied.
 */
function hasCoordinates(hike) {
  return Number.isFinite(Number(hike?.latitude)) && Number.isFinite(Number(hike?.longitude));
}

/**
 * Renders the hike map preview experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.hike - hike value.
 * @returns {JSX.Element} The rendered interface.
 */
function HikeMapPreview({ hike }) {
  if (!hasCoordinates(hike)) return null;
  const lat = Number(hike.latitude);
  const lon = Number(hike.longitude);
  const delta = 0.025;
  const bbox = [lon - delta, lat - delta, lon + delta, lat + delta].join(',');
  const title = hike.title || hikingStrings.module.untitled;
  const src = `https://www.openstreetmap.org/export/embed.html?bbox=${encodeURIComponent(bbox)}&layer=mapnik&marker=${encodeURIComponent(`${lat},${lon}`)}`;

  return (
    <div className="hike-map-preview detail-map-preview" aria-label={`${title} map preview`}>
      <iframe title={`${title} map preview`} src={src} loading="lazy" />
    </div>
  );
}

/** Owns hike lists, trail search/enrichment, route-based detail flows, and mutations. */
export default function HikingModule({
  isAdding,
  setIsAdding,
  theme,
  onClose,
  onRegisterCloseGuard,
  onHeaderActionsChange,
  onHeaderTitleChange,
  modulePath = '',
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const targetItemId = searchParams.get('itemId');
  const openedTargetRef = useRef(null);
  const [importedTrail, setImportedTrail] = useState(null);
  const [isEnrichingSelectedTrail, setIsEnrichingSelectedTrail] = useState(false);
  const [manualHikeEntry, setManualHikeEntry] = useState(false);
  const [selectedHike, setSelectedHike] = useState(null);
  const [editingHike, setEditingHike] = useState(null);
  const [hikePendingDelete, setHikePendingDelete] = useState(null);
  const [hasFormChanges, setHasFormChanges] = useState(false);
  const [search, setSearch] = useState('');
  const [activeStatus, setActiveStatus] = useState('');
  const [activeDifficulty, setActiveDifficulty] = useState('');
  const [activeTag, setActiveTag] = useState('');
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [showPremiumUpsell, setShowPremiumUpsell] = useState(false);
  const routeParts = useMemo(
    () => String(modulePath || '').split('/').map((part) => decodeURIComponent(part)).filter(Boolean),
    [modulePath]
  );
  const isCreateRoute = routeParts[0] === 'new';
  const routeHikeId = routeParts[0] && routeParts[0] !== 'new' ? routeParts[0] : '';
  const isEditRoute = Boolean(routeHikeId && routeParts[1] === 'edit');
  const isAddFlow = isAdding || isCreateRoute;
  const subscriptionRefetch = [{ query: GET_MY_SUBSCRIPTION_STATUS }];

  const { loading, error, data, fetchMore } = useQuery(GET_HIKES, {
    variables: { first: HIKING_PAGE_SIZE, after: null },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  const { data: tripsData } = useQuery(GET_TRIPS, {
    variables: { first: 50, after: null },
    errorPolicy: 'all',
  });
  const { data: subscriptionData } = useQuery(GET_MY_SUBSCRIPTION_STATUS, {
    fetchPolicy: 'cache-and-network',
  });
  const [searchTrails, {
    data: trailSearchData,
    loading: isSearchingTrails,
    error: trailSearchError,
  }] = useLazyQuery(SEARCH_TRAILS);
  const [loadHikeDetail] = useLazyQuery(GET_HIKE, { fetchPolicy: 'network-only' });
  const hikes = useMemo(() => (data?.getHikesListPage?.nodes || []).map(parseHike), [data]);
  const trips = useMemo(() => tripsData?.getTripsListPage?.nodes || [], [tripsData]);
  const hikesPageInfo = data?.getHikesListPage?.pageInfo || {};
  const subscription = subscriptionData?.getMySubscriptionStatus;
  const {
    loading: isLoadingSelectedHike,
    data: selectedHikeData,
    refetch: refetchSelectedHike,
  } = useQuery(GET_HIKE, {
    variables: { id: selectedHike?.id || '' },
    skip: !selectedHike?.id,
    errorPolicy: 'all',
  });
  const selectedHikeRecord = selectedHikeData && selectedHikeData.getHike;
  const selectedHikeDetail = selectedHikeRecord && selectedHikeRecord.id === selectedHike?.id
    ? parseHike(selectedHikeRecord)
    : selectedHike;
  const statuses = useMemo(() => uniqueValues(hikes, (hike) => [hike.status]), [hikes]);
  const difficulties = useMemo(() => uniqueValues(hikes, (hike) => [hike.difficulty]), [hikes]);
  const tags = useMemo(() => uniqueValues(hikes, (hike) => hike.tags), [hikes]);

  const sortedHikes = useMemo(() => (
    [...hikes].sort((a, b) => String(b.hikeDate || '').localeCompare(String(a.hikeDate || '')))
  ), [hikes]);

  const filteredHikes = useMemo(() => {
    const query = search.trim().toLowerCase();
    return sortedHikes.filter((hike) => {
      const searchable = [
        hike.title,
        hike.location,
        hike.hikeDate,
        hike.status,
        hike.difficulty,
        hike.trailType,
        hike.terrain,
        hike.tripTitle,
        hike.weather,
        hike.trailCondition,
        hike.crowdLevel,
        hike.notes,
        hike.tags.join(' '),
      ].join(' ').toLowerCase();

      return (
        (!query || searchable.includes(query)) &&
        (!activeStatus || hike.status === activeStatus) &&
        (!activeDifficulty || hike.difficulty === activeDifficulty) &&
        (!activeTag || hike.tags.includes(activeTag)) &&
        (!favoritesOnly || hike.favorite)
      );
    });
  }, [activeDifficulty, activeStatus, activeTag, favoritesOnly, search, sortedHikes]);

  const totalMinutes = hikes.reduce((sum, hike) => sum + Number(hike.durationMinutes || 0), 0);

  const [addHike, { loading: isSaving }] = useMutation(ADD_HIKE, {
    refetchQueries: [{ query: GET_HIKES, variables: { first: HIKING_PAGE_SIZE, after: null } }, ...subscriptionRefetch],
    onCompleted: () => {
      setHasFormChanges(false);
    },
  });
  const [saveAlbumLink] = useMutation(SAVE_ENTRY_PHOTO_ALBUM_LINK);
  const [addHighlightPhoto] = useMutation(ADD_ENTRY_HIGHLIGHT_PHOTO);

  const [updateHike, { loading: isUpdating }] = useMutation(UPDATE_HIKE, {
    refetchQueries: [{ query: GET_HIKES, variables: { first: HIKING_PAGE_SIZE, after: null } }],
    onCompleted: (result) => {
      setEditingHike(null);
      setHasFormChanges(false);
      if (result?.updateHike) {
        setSelectedHike(parseHike(result.updateHike));
      }
    },
  });

  const [deleteHike, { loading: isDeleting }] = useMutation(DELETE_HIKE, {
    refetchQueries: [{ query: GET_HIKES, variables: { first: HIKING_PAGE_SIZE, after: null } }, ...subscriptionRefetch],
    onCompleted: () => {
      setHikePendingDelete(null);
      setSelectedHike(null);
      navigate('/hobby/hiking');
    },
  });
  const [enrichTrail] = useMutation(ENRICH_TRAIL);
  const [uploadEntryCoverPhoto, { loading: isUploadingPhoto }] = useMutation(UPLOAD_ENTRY_COVER_PHOTO);
  const [removeEntryCoverPhoto, { loading: isRemovingPhoto }] = useMutation(REMOVE_ENTRY_COVER_PHOTO);

  /**
   * Performs hike photo.
   *
   * @param {*} formData - form data value.
   * @param {string} hikeId - hike id value.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const syncHikePhoto = async (formData, hikeId) => {
    if (!hikeId) return;
    const refetchQueries = [
      { query: GET_HIKES, variables: { first: HIKING_PAGE_SIZE, after: null } },
      { query: GET_HIKE, variables: { id: hikeId } },
    ];

    if (formData.coverPhotoFile) {
      await uploadEntryCoverPhoto({
        variables: {
          input: {
            hobbyId: 'hiking',
            entryId: hikeId,
            contentType: formData.coverPhotoFile.type || 'image/jpeg',
            base64Data: await fileToBase64(formData.coverPhotoFile),
          },
        },
        refetchQueries,
        awaitRefetchQueries: true,
      });
      return;
    }

    if (formData.shouldRemoveCoverPhoto) {
      await removeEntryCoverPhoto({
        variables: { hobbyId: 'hiking', entryId: hikeId },
        refetchQueries,
        awaitRefetchQueries: true,
      });
    }
  };

  /**
   * Normalizes input.
   *
   * @param {*} formData - form data value.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const cleanInput = (formData) => ({
    title: formData.title.trim(),
    location: formData.location.trim(),
    hikeDate: formData.hikeDate,
    status: formData.status,
    distance: formData.distance.trim(),
    distanceUnit: formData.distanceUnit,
    elevationGain: Number(formData.elevationGain || 0),
    elevationUnit: formData.elevationUnit,
    durationMinutes: Number(formData.durationMinutes || 0),
    difficulty: formData.difficulty,
    trailType: formData.trailType,
    terrain: formData.terrain,
    latitude: formData.latitude ?? null,
    longitude: formData.longitude ?? null,
    hasRouteGeometry: Boolean(formData.hasRouteGeometry),
    geometrySummary: formData.geometrySummary || '',
    routeUrl: formData.routeUrl.trim(),
    mapUrl: formData.mapUrl.trim(),
    permitRequired: Boolean(formData.permitRequired),
    dogFriendly: Boolean(formData.dogFriendly),
    kidFriendly: Boolean(formData.kidFriendly),
    rating: formData.rating ? Number(formData.rating) : null,
    startTime: formData.startTime,
    parkingNotes: formData.parkingNotes.trim(),
    companions: formData.companions.trim(),
    weather: formData.weather.trim(),
    trailCondition: formData.trailCondition.trim(),
    crowdLevel: formData.crowdLevel,
    actualDistance: formData.actualDistance.trim(),
    actualDurationMinutes: Number(formData.actualDurationMinutes || 0),
    notes: formData.notes.trim(),
    checklist: (formData.checklist || []).filter((item) => item.title.trim()).map((item) => ({
      id: item.id,
      title: item.title.trim(),
      packed: Boolean(item.packed),
    })),
    tags: splitCsv(formData.tags),
    tripId: formData.tripId || '',
    tripTitle: formData.tripTitle.trim(),
    tripDayId: formData.tripDayId || '',
    tripDayTitle: formData.tripDayTitle?.trim() || '',
    trailCatalogId: formData.trailCatalogId || '',
    trailSource: formData.trailSource || '',
    trailSourceId: formData.trailSourceId || '',
    favorite: Boolean(formData.favorite),
  });

  /**
   * Handles save.
   *
   * @param {*} formData - form data value.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleSave = async (formData) => {
    const input = cleanInput(formData);
    try {
      if (formData.id) {
        await updateHike({ variables: { id: formData.id, input } });
        await syncHikePhoto(formData, formData.id);
        navigate(`/hobby/hiking/${encodeURIComponent(formData.id)}`);
        return;
      }
      const result = await addHike({ variables: { input } });
      const entryId = result.data?.addHike?.id;
      await syncHikePhoto(formData, entryId);
      for (const file of formData.highlightDrafts || []) {
        try {
          await addHighlightPhoto({ variables: { input: { hobbyId: 'hiking', entryId, contentType: file.type, base64Data: await fileToBase64(file), visibility: 'owner' } } });
        } catch (photoError) {
          window.alert(`${entryPhotoStrings.hikeSavedWithoutPhoto} ${getPublicErrorMessage(photoError)}`);
          break;
        }
      }
      try { await saveCreatedAlbumLink(saveAlbumLink, 'hiking', entryId, formData.albumDraft); }
      catch { window.alert(entryPhotoStrings.hikeSavedWithoutAlbum); }
      navigate('/hobby/hiking');
      onClose();
    } catch (saveError) {
      if (isLimitReachedError(saveError)) {
        setShowPremiumUpsell(true);
        return;
      }
      window.alert(getPublicErrorMessage(saveError));
    }
  };

  const closeForm = useCallback(() => {
    const editingHikeId = editingHike?.id;
    setImportedTrail(null);
    setIsEnrichingSelectedTrail(false);
    setManualHikeEntry(false);
    setEditingHike(null);
    setHasFormChanges(false);
    navigate(editingHikeId ? `/hobby/hiking/${encodeURIComponent(editingHikeId)}` : '/hobby/hiking');
    onClose();
  }, [editingHike?.id, navigate, onClose]);

  const handleCancelForm = useCallback(({ confirmDiscard } = {}) => {
    if (confirmDiscard && hasFormChanges) {
      const shouldDiscard = window.confirm(hikingStrings.module.discardConfirm);
      if (!shouldDiscard) return;
    }
    closeForm();
  }, [closeForm, hasFormChanges]);

  /**
   * Handles confirm delete.
   *
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleConfirmDelete = async () => {
    if (!hikePendingDelete) return;
    await deleteHike({ variables: { id: hikePendingDelete.id } });
  };

  /**
   * Handles load more.
   *
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleLoadMore = async () => {
    if (!hikesPageInfo.hasNextPage || !hikesPageInfo.endCursor) return;
    await fetchMore({
      variables: {
        first: HIKING_PAGE_SIZE,
        after: hikesPageInfo.endCursor,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult?.getHikesListPage) return previousResult;
        return {
          getHikesListPage: {
            ...fetchMoreResult.getHikesListPage,
            nodes: [
              ...(previousResult.getHikesListPage?.nodes || []),
              ...(fetchMoreResult.getHikesListPage.nodes || []),
            ],
          },
        };
      },
    });
  };

  const handleSearchTrails = useCallback((query) => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) return;
    searchTrails({ variables: { query: trimmedQuery } });
  }, [searchTrails]);

  /**
   * Performs the trail to form data operation.
   *
   * @param {*} trail - trail value.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const trailToFormData = (trail) => ({
    title: trail.title,
    location: trail.location || '',
    distance: trail.distance || '',
    distanceUnit: trail.distanceUnit || 'mi',
    elevationGain: trail.elevationGain || '',
    elevationUnit: trail.elevationUnit || 'ft',
    durationMinutes: trail.durationMinutes || '',
    difficulty: trail.difficulty || 'Moderate',
    trailType: trail.trailType || 'Loop',
    terrain: trail.terrain || '',
    latitude: trail.latitude ?? null,
    longitude: trail.longitude ?? null,
    hasRouteGeometry: Boolean(trail.hasRouteGeometry),
    geometrySummary: trail.geometrySummary || '',
    routeUrl: trail.routeUrl || '',
    mapUrl: trail.mapUrl || '',
    permitRequired: Boolean(trail.permitRequired),
    dogFriendly: Boolean(trail.dogFriendly),
    kidFriendly: Boolean(trail.kidFriendly),
    notes: '',
    tags: [],
    selectedTrailName: trail.title || '',
    status: 'Planned',
    trailCatalogId: trail.trailCatalogId || '',
    trailSource: trail.source || '',
    trailSourceId: trail.sourceId || '',
  });

  /**
   * Handles use trail.
   *
   * @param {*} trail - trail value.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleUseTrail = async (trail) => {
    if (!canStartCreateFlow()) return;
    setImportedTrail({
      ...trailToFormData(trail),
    });
    setManualHikeEntry(false);
    setIsAdding(true);
    navigate('/hobby/hiking/new');
    setIsEnrichingSelectedTrail(true);

    try {
      const result = await enrichTrail({
        variables: {
          input: {
            trailId: trail.trailId,
            trailCatalogId: trail.trailCatalogId || '',
            source: trail.source || '',
            sourceId: trail.sourceId || '',
            title: trail.title,
            location: trail.location || '',
            latitude: trail.latitude || null,
            longitude: trail.longitude || null,
            distance: trail.distance || '',
            distanceUnit: trail.distanceUnit || '',
            elevationGain: trail.elevationGain || null,
            elevationUnit: trail.elevationUnit || '',
            durationMinutes: trail.durationMinutes || null,
            difficulty: trail.difficulty || '',
            trailType: trail.trailType || '',
            terrain: trail.terrain || '',
            routeUrl: trail.routeUrl || '',
            mapUrl: trail.mapUrl || '',
            permitRequired: trail.permitRequired,
            dogFriendly: trail.dogFriendly,
            kidFriendly: trail.kidFriendly,
            tags: trail.tags || [],
            notes: trail.notes || '',
            attribution: trail.attribution || '',
            fetchedAt: trail.fetchedAt || '',
          },
        },
      });
      const selectedTrail = result?.data?.enrichTrail || trail;
      setImportedTrail({
        ...trailToFormData(selectedTrail),
      });
    } catch (error) {
      setImportedTrail((current) => current || {
        ...trailToFormData(trail),
      });
    } finally {
      setIsEnrichingSelectedTrail(false);
    }
  };

  /**
   * Handles start add.
   *
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const handleStartAdd = () => {
    if (!canStartCreateFlow()) return;
    setImportedTrail(null);
    setManualHikeEntry(false);
    setIsAdding(true);
    navigate('/hobby/hiking/new');
  };

  /**
   * Determines whether start create flow.
   *
   * @returns {boolean} Whether the requested condition is satisfied.
   */
  const canStartCreateFlow = () => {
    if (canCreateEntries(subscription, 'hiking')) return true;
    setShowPremiumUpsell(true);
    return false;
  };

  useEffect(() => {
    if (isAddFlow || editingHike) {
      onHeaderActionsChange?.(null);
      return () => onHeaderActionsChange?.(null);
    }

    if (selectedHikeDetail) {
      onHeaderActionsChange?.(
        <DetailActionMenu
          label="Hike actions"
          primaryAction={{ label: 'Edit', disabled: isLoadingSelectedHike, onSelect: () => handleEditHike(selectedHikeDetail) }}
          actions={[
            { label: shareHighlightStrings.action, onSelect: () => openShareHighlight(buildShareHighlight('hiking', selectedHikeDetail)) },
            { label: hikingStrings.module.deleteAction, variant: 'danger', onSelect: () => setHikePendingDelete(selectedHikeDetail) },
          ]}
        />
      );
      return () => onHeaderActionsChange?.(null);
    }

    onHeaderActionsChange?.(
      <div className="recipe-actions">
        <button className="btn-primary" type="button" onClick={handleStartAdd}>{hikingStrings.module.addAction}</button>
      </div>
    );
    return () => onHeaderActionsChange?.(null);
  }, [editingHike, isAddFlow, isLoadingSelectedHike, onHeaderActionsChange, selectedHikeDetail, subscription]);

  useEffect(() => {
    if (isEditRoute || editingHike) {
      onHeaderTitleChange?.(hikingStrings.module.editAction);
    } else if (isCreateRoute || isAdding) {
      onHeaderTitleChange?.(hikingStrings.module.addAction);
    } else if (routeHikeId || selectedHikeDetail) {
      onHeaderTitleChange?.(selectedHikeDetail?.title || 'Hike');
    } else {
      onHeaderTitleChange?.('');
    }

    return () => onHeaderTitleChange?.('');
  }, [editingHike, isAdding, isCreateRoute, isEditRoute, onHeaderTitleChange, routeHikeId, selectedHikeDetail]);

  /**
   * Renders premium upsell.
   *
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const renderPremiumUpsell = () => showPremiumUpsell && (
    <PremiumUpsellModal
      hobbyId="hiking"
      subscription={subscription}
      onClose={() => setShowPremiumUpsell(false)}
      onOpenAccount={() => {
        setShowPremiumUpsell(false);
        window.location.assign('/account');
      }}
    />
  );

  const fetchHikeDetail = useCallback(async (hike) => {
    const result = await loadHikeDetail({ variables: { id: hike.id } });
    return parseHike(result.data?.getHike || hike);
  }, [loadHikeDetail]);

  /**
   * Handles select hike.
   *
   * @param {*} hike - hike value.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const handleSelectHike = (hike) => {
    setSelectedHike(hike);
    navigate(`/hobby/hiking/${encodeURIComponent(hike.id)}`);
  };

  /**
   * Handles edit hike.
   *
   * @param {*} hike - hike value.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleEditHike = async (hike) => {
    navigate(`/hobby/hiking/${encodeURIComponent(hike.id)}/edit`);
    setEditingHike(await fetchHikeDetail(hike));
  };

  useEffect(() => {
    if (isCreateRoute) {
      setSelectedHike(null);
      setEditingHike(null);
      setIsAdding(true);
      return;
    }

    if (!routeHikeId) {
      setSelectedHike(null);
      setEditingHike(null);
      setImportedTrail(null);
      setManualHikeEntry(false);
      setIsAdding(false);
      return;
    }

    setIsAdding(false);
    const hike = hikes.find((item) => item.id === routeHikeId) || { id: routeHikeId };
    fetchHikeDetail(hike)
      .then((detail) => {
        if (isEditRoute) {
          setSelectedHike(null);
          setEditingHike(detail);
          return;
        }
        setEditingHike(null);
        setSelectedHike(detail);
      })
      .catch(() => navigate('/hobby/hiking', { replace: true }));
  }, [fetchHikeDetail, hikes, isCreateRoute, isEditRoute, navigate, routeHikeId, setIsAdding]);

  useEffect(() => {
    if (!targetItemId || openedTargetRef.current === targetItemId) return;
    openedTargetRef.current = targetItemId;
    navigate(`/hobby/hiking/${encodeURIComponent(targetItemId)}`, { replace: true });
  }, [navigate, targetItemId]);

  useEffect(() => {
    if (isAddFlow || editingHike) {
      onRegisterCloseGuard?.(() => handleCancelForm({ confirmDiscard: true }));
      return () => onRegisterCloseGuard?.(null);
    }
    if (selectedHike) {
      onRegisterCloseGuard?.(() => navigate('/hobby/hiking'));
      return () => onRegisterCloseGuard?.(null);
    }
    onRegisterCloseGuard?.(null);
    return undefined;
  }, [editingHike, handleCancelForm, isAddFlow, navigate, onRegisterCloseGuard, selectedHike]);

  if (isAddFlow && !editingHike && !importedTrail && !manualHikeEntry) {
    return (
      <>
        <TrailAddSearch
          results={trailSearchData?.searchTrails || []}
          onCancel={handleCancelForm}
          onManualEntry={() => {
            if (!canStartCreateFlow()) return;
            setManualHikeEntry(true);
          }}
          onSearch={handleSearchTrails}
          onUseTrail={handleUseTrail}
          isSearching={isSearchingTrails}
          error={trailSearchError}
        />
        {renderPremiumUpsell()}
      </>
    );
  }

  if (isAddFlow || editingHike) {
    return (
      <>
        <HikingForm
          theme={theme}
          onCancel={handleCancelForm}
          onSave={handleSave}
          initialData={editingHike || importedTrail}
          trips={trips}
          onDirtyChange={setHasFormChanges}
          isEditing={Boolean(editingHike)}
          onLimitReached={() => setShowPremiumUpsell(true)}
          isSaving={isSaving || isUpdating || isUploadingPhoto || isRemovingPhoto}
          isEnrichingTrail={isEnrichingSelectedTrail}
        />
        {renderPremiumUpsell()}
      </>
    );
  }

  if (selectedHikeDetail) {
    return (
      <div className="module-wrapper hiking-module movies-module">
        <div className="recipe-detail-view">
          <button type="button" className="recipe-back-link" onClick={() => navigate('/hobby/hiking')}>← Back</button>

          {hikePendingDelete && (
            <div className="modal-overlay">
              <div className="modal-content delete-confirmation">
                <p className="eyebrow">{hikingStrings.module.deleteTitle}</p>
                <h2>{hikingStrings.module.deletePrompt}</h2>
                <p>{hikingStrings.module.deleteDetail(hikePendingDelete.title)}</p>
                <div className="form-actions">
                  <button type="button" className="btn-ghost" onClick={() => setHikePendingDelete(null)}>{moduleCommonStrings.actions.cancel}</button>
                  <button type="button" className="btn-danger solid" onClick={handleConfirmDelete} disabled={isDeleting}>
                    {isDeleting ? moduleCommonStrings.actions.deleting : hikingStrings.module.deleteAction}
                  </button>
                </div>
              </div>
            </div>
          )}

          <section className="recipe-detail-hero">
            <EntryImage
              className="entry-detail-photo"
              src={selectedHikeDetail.coverPhotoUrl}
              title={selectedHikeDetail.title}
              icon="⌃"
              fallbackType="hiking"
              variant="detail-square"
            />
            <div>
              <p className="eyebrow">{formatDate(selectedHikeDetail.hikeDate)}</p>
              <h2>{selectedHikeDetail.title}</h2>
              <p>{[selectedHikeDetail.location, selectedHikeDetail.status, selectedHikeDetail.difficulty, selectedHikeDetail.terrain].filter(Boolean).join(' - ')}</p>
            </div>
            <div className="recipe-detail-facts">
              <span>{distanceLabel(selectedHikeDetail)}</span>
              <span>{durationLabel(selectedHikeDetail.durationMinutes)}</span>
              {selectedHikeDetail.actualDistance && <span>Actual {selectedHikeDetail.actualDistance} {selectedHikeDetail.distanceUnit}</span>}
              {selectedHikeDetail.actualDurationMinutes > 0 && <span>Actual {durationLabel(selectedHikeDetail.actualDurationMinutes)}</span>}
              {selectedHikeDetail.elevationGain > 0 && <span>{selectedHikeDetail.elevationGain} {selectedHikeDetail.elevationUnit} gain</span>}
              {selectedHikeDetail.rating && <RatingReadout rating={selectedHikeDetail.rating} />}
              {selectedHikeDetail.permitRequired && <span>Permit required</span>}
              {selectedHikeDetail.dogFriendly && <span>Dog friendly</span>}
              {selectedHikeDetail.kidFriendly && <span>Kid friendly</span>}
              {selectedHikeDetail.favorite && <span>{hikingStrings.module.favorite}</span>}
            </div>
          </section>

          <div className="recipe-detail-grid">
            <PhotoAlbumsSection hobbyId="hiking" entryId={selectedHikeDetail.id} canManage={false} onLimitReached={() => setShowPremiumUpsell(true)} />
            <section className="recipe-detail-panel">
              <h3>Planning</h3>
              <HikeMapPreview hike={selectedHikeDetail} />
              <dl className="movie-info-list">
                <div><dt>Start time</dt><dd>{selectedHikeDetail.startTime || 'Not added'}</dd></div>
                <div><dt>Parking</dt><dd>{selectedHikeDetail.parkingNotes || 'Not added'}</dd></div>
                <div><dt>Companions</dt><dd>{selectedHikeDetail.companions || 'Not added'}</dd></div>
                <div><dt>Linked trip</dt><dd>{selectedHikeDetail.tripTitle || 'Not linked'}</dd></div>
                <div><dt>Trip day</dt><dd>{selectedHikeDetail.tripDayTitle || 'Not linked'}</dd></div>
              </dl>
              {(selectedHikeDetail.routeUrl || selectedHikeDetail.mapUrl) && (
                <div className="map-action-row">
                  {selectedHikeDetail.mapUrl && <a className="map-action-button" href={selectedHikeDetail.mapUrl} target="_blank" rel="noreferrer">{hikingStrings.module.openMap}</a>}
                  {selectedHikeDetail.routeUrl && <a className="map-action-button" href={selectedHikeDetail.routeUrl} target="_blank" rel="noreferrer">{hikingStrings.module.openRoute}</a>}
                </div>
              )}
            </section>

            <section className="recipe-detail-panel">
              <h3>Trail log</h3>
              <dl className="movie-info-list">
                <div><dt>Weather</dt><dd>{selectedHikeDetail.weather || 'Not added'}</dd></div>
                <div><dt>Condition</dt><dd>{selectedHikeDetail.trailCondition || 'Not added'}</dd></div>
                <div><dt>Crowds</dt><dd>{selectedHikeDetail.crowdLevel || 'Not added'}</dd></div>
              </dl>
              <p className="detail-notes">{selectedHikeDetail.notes || hikingStrings.module.noNotes}</p>
            </section>
          </div>

          {selectedHikeDetail.checklist.length > 0 && (
            <section className="recipe-detail-panel">
              <h3>Packing checklist</h3>
              <EntryChecklistProgress hobbyId="hiking" entryId={selectedHikeDetail.id} items={selectedHikeDetail.checklist} canEdit onChanged={() => refetchSelectedHike()} />
            </section>
          )}

          <section className="recipe-detail-panel">
            <h3>Tags</h3>
            <div className="recipe-tags">
              {selectedHikeDetail.tags.length ? selectedHikeDetail.tags.map((tag) => <span key={tag}>{tag}</span>) : <span>No tags yet</span>}
            </div>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="module-wrapper hiking-module movies-module">
      <div className="recipe-library-layout">
        <aside className="recipe-books-panel">
          <p className="eyebrow">{hikingStrings.module.statusEyebrow}</p>
          <div className="recipe-book-list">
            <button type="button" className={!activeStatus ? 'active' : ''} onClick={() => setActiveStatus('')}>
              <span>{hikingStrings.module.allHikes}</span>
              <strong>{hikes.length}</strong>
            </button>
            {statuses.map((status) => (
              <button type="button" key={status} className={activeStatus === status ? 'active' : ''} onClick={() => setActiveStatus(status)}>
                <span>{status}</span>
                <strong>{hikes.filter((hike) => hike.status === status).length}</strong>
              </button>
            ))}
          </div>
        </aside>

        <section className="recipe-main-panel">
          <div className="recipe-filters">
            <div className="form-group search-field">
              <label>{hikingStrings.module.searchLabel}</label>
              <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={hikingStrings.module.searchPlaceholder} />
            </div>
            <div className="filter-row trip-filter-row">
              <label>
                {hikingStrings.module.difficultyLabel}
                <select value={activeDifficulty} onChange={(event) => setActiveDifficulty(event.target.value)}>
                  <option value="">{hikingStrings.module.anyDifficulty}</option>
                  {difficulties.map((difficulty) => <option key={difficulty} value={difficulty}>{difficulty}</option>)}
                </select>
              </label>
              <label>
                {hikingStrings.module.tagLabel}
                <select value={activeTag} onChange={(event) => setActiveTag(event.target.value)}>
                  <option value="">{hikingStrings.module.anyTag}</option>
                  {tags.map((tag) => <option key={tag} value={tag}>{tag}</option>)}
                </select>
              </label>
              <button type="button" className={`favorite-filter ${favoritesOnly ? 'active' : ''}`} onClick={() => setFavoritesOnly((current) => !current)}>
                {moduleCommonStrings.filters.favorites}
              </button>
            </div>
          </div>

          {loading && hikes.length === 0 && (
            <div className="module-loading-state">
              <ModuleListSkeleton />
            </div>
          )}
          {error && <p className="error-copy">Error: {getPublicErrorMessage(error)}</p>}

          <div className="recipe-stats">
            <span>{hikingStrings.module.shown(filteredHikes.length)}</span>
            <span>{hikingStrings.module.minutes(totalMinutes)}</span>
            <span>{hikingStrings.module.completed(hikes.filter((hike) => hike.status === 'Completed').length)}</span>
          </div>

          <div className="recipe-card-grid">
            {filteredHikes.map((hike) => (
              <button key={hike.id} type="button" className="recipe-card hiking-card has-entry-photo" onClick={() => handleSelectHike(hike)}>
                <EntryImage src={hike.coverPhotoUrl} title={hike.title} icon="⌃" fallbackType="hiking" />
                <div>
                  <div className="movie-card-heading">
                    <h3>{hike.title}</h3>
                    {hike.favorite && <span className="favorite-chip">{hikingStrings.module.favorite}</span>}
                  </div>
                  <p>{[hike.location, formatDate(hike.hikeDate), hike.status].filter(Boolean).join(' • ')}</p>
                  <p className="movie-card-detail-line">
                    {[distanceLabel(hike), durationLabel(hike.durationMinutes), hike.difficulty].filter(Boolean).join(' • ')}
                  </p>
                </div>
              </button>
            ))}
          </div>

          {hikesPageInfo.hasNextPage && (
            <div className="load-more-row">
              <button type="button" className="btn-secondary" onClick={handleLoadMore} disabled={loading}>
                {loading ? hikingStrings.module.loadingMore : hikingStrings.module.loadMore}
              </button>
            </div>
          )}

          {filteredHikes.length === 0 && !loading && (
            <HobbyEmptyState hobbyId="hiking" filtered={hikes.length > 0}
              title={hikes.length === 0 ? moduleCommonStrings.empty.nothingSaved : hikingStrings.module.emptyTitle}
              detail={hikes.length === 0 ? hikingStrings.module.emptyFirst : hikingStrings.module.empty} />
          )}
        </section>
      </div>
      {renderPremiumUpsell()}
    </div>
  );
}
